const ALLOWED_TAGS = new Set(["B", "STRONG", "I", "EM", "U", "P", "BR", "UL", "OL", "LI"]);

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export function sanitizeRichText(value: string): string {
  if (typeof DOMParser === "undefined") return escapeHtml(value);
  const document = new DOMParser().parseFromString(value, "text/html");
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT);
  const elements: Element[] = [];
  while (walker.nextNode()) elements.push(walker.currentNode as Element);

  for (const element of elements) {
    // Pressing Enter in a contenteditable usually makes a DIV per line. Kept
    // as a paragraph so the lines stay apart instead of running together.
    if (element.tagName === "DIV") {
      const paragraph = document.createElement("p");
      paragraph.append(...Array.from(element.childNodes));
      element.replaceWith(paragraph);
      continue;
    }
    if (!ALLOWED_TAGS.has(element.tagName)) {
      element.replaceWith(...Array.from(element.childNodes));
      continue;
    }
    for (const attribute of Array.from(element.attributes)) element.removeAttribute(attribute.name);
  }

  return document.body.innerHTML;
}

export function richTextHtml(value: string | undefined): string {
  if (!value) return "";
  if (!/<[a-z][\s\S]*>/i.test(value)) return escapeHtml(value).replace(/\n/g, "<br />");
  return sanitizeRichText(value);
}

/**
 * The text a reader sees, without tags: for length limits and for places that
 * show a description on one line. Line breaks, paragraphs, lists and list
 * items are kept as single newlines, so their words don't run together.
 */
export function richTextPlain(value: string | undefined): string {
  if (!value) return "";
  if (!/<[a-z][\s\S]*>/i.test(value)) return value;
  if (typeof DOMParser === "undefined") return value.replace(/<[^>]*>/g, "");
  const document = new DOMParser().parseFromString(sanitizeRichText(value), "text/html");
  for (const element of Array.from(document.body.querySelectorAll("br, p, ul, ol, li"))) {
    if (element.tagName === "BR") element.replaceWith("\n");
    else element.append("\n");
    if (element.tagName === "UL" || element.tagName === "OL") element.prepend("\n");
  }
  return (document.body.textContent ?? "").replace(/\n{2,}/g, "\n").replace(/^\n+|\n+$/g, "");
}
